// Loading a set of the commissioned terrain in the browser (docs/166 B): only the set a fight needs, when it
// starts (and again if its hour crosses into night). The set's JSON record and atlas come from the build's
// `virtual:fk-encounter-atlas` (packed on demand by the dev server); the atlas is decoded once to RGBA
// (`decode.ts`) so the painter can read every piece's pixels, and uploaded to the GPU straight from those
// bytes (docs/186, performance: a canvas texture read the whole atlas back, ~360 ms on a busy GPU). At most four
// sets stay in memory (a fight's day and night, and the next fight's; a place's country beside it, EA14); a scene
// releases the textures of the sets it no longer holds.
import Phaser from 'phaser';
import atlas from 'virtual:fk-encounter-atlas';
import { DataTexture } from '../board/dataTexture';
import { fetchSetFrom, framesOf, setFrom } from './decode';
import { setFilesViaWorker } from './painter';
import { keepsLit, isLit, toGl } from './prepare';
import type { EncounterSet } from './types';

export { frameName, setFrom } from './decode';

const KEEP = 4;
const loaded = new Map<string, Promise<EncounterSet>>();

/** The set's texture key in a scene. */
export const textureKey = (id: string): string => `enc:${id}`;
/** The texture of the set's lit pixels (docs/164 ET9), frames as the set's. */
export const litTextureKey = (id: string): string => `enc:${id}:lit`;

/** A set's record and pixels; cached per id (the four most recently asked for). */
export function loadSet(id: string): Promise<EncounterSet> {
  const have = loaded.get(id);
  if (have) {
    loaded.delete(id);
    loaded.set(id, have);
    return have;
  }
  const urls = atlas.sets[id];
  // through the board painter's worker when there is one (it paints from the same files), else fetched here
  const viaWorker = urls ? setFilesViaWorker(id)?.then((f) => setFrom(f.record, f.data, f.width), () => fetchSetFrom(urls)) : undefined;
  const p = viaWorker ?? (urls ? fetchSetFrom(urls) : Promise.reject(new Error(`no encounter set ${id}`)));
  loaded.set(id, p);
  p.catch(() => { if (loaded.get(id) === p) loaded.delete(id); });
  while (loaded.size > KEEP) loaded.delete(loaded.keys().next().value!);
  return p;
}

/** The frames of a set with lit pixels, once per set (night and indoor sets only). */
const litFrames = new WeakMap<EncounterSet, ReadonlySet<string>>();
/** Who holds each set texture (textures are the game's, shared by its scenes). */
const holders = new Map<string, Set<object>>();

/** Adds the set's atlas to the scene as texture `enc:<id>`, one frame per file (`Piece.frame`); idempotent. */
export function registerSetTexture(scene: Phaser.Scene, set: EncounterSet, holder?: object): string {
  const key = textureKey(set.id);
  if (holder) { let h = holders.get(key); if (!h) holders.set(key, (h = new Set())); h.add(holder); }
  if (!scene.textures.exists(key)) {
    const at = framesOf(set);
    if (!at) throw new Error(`encounter set ${set.id} was not made by setFrom`);
    let tex: Phaser.Textures.Texture | null;
    if (set.atlas && scene.game.renderer.type === Phaser.WEBGL) {
      // straight from the decoded bytes: one upload, nothing read back
      new DataTexture(scene, key, set.atlas.width, set.atlas.height, toGl(new Uint8ClampedArray(set.atlas.rgba), set.atlas.width, set.atlas.height), false, true);
      tex = scene.textures.get(key);
    } else {
      // no WebGL: the atlas's bytes onto a canvas
      let source: TexImageSource | undefined = set.image;
      if (!source && set.atlas && typeof document !== 'undefined') {
        const c = document.createElement('canvas');
        c.width = set.atlas.width; c.height = set.atlas.height;
        c.getContext('2d')?.putImageData(new ImageData(new Uint8ClampedArray(set.atlas.rgba), set.atlas.width, set.atlas.height), 0, 0);
        source = c;
      }
      if (!source) throw new Error(`encounter set ${set.id} has no atlas image`);
      // WebGL ignores UNPACK_FLIP_Y for an ImageBitmap, but Phaser's frame UVs expect every source uploaded
      // flipped: an ImageBitmap's frames came out of the mirrored rows. A canvas copy uploads as Phaser expects.
      if (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap && typeof document !== 'undefined') {
        const c = document.createElement('canvas');
        c.width = source.width; c.height = source.height;
        c.getContext('2d')?.drawImage(source, 0, 0);
        source = c;
      }
      tex = source instanceof HTMLCanvasElement ? scene.textures.addCanvas(key, source) : scene.textures.addImage(key, source as unknown as HTMLImageElement);
    }
    if (!tex) throw new Error(`could not add texture ${key}`);
    for (const [frame, r] of at) tex.add(frame, 0, r.x, r.y, r.w, r.h);
  }
  set.texture = key;
  return key;
}

/**
 * docs/164 ET9 for the set's own pieces: texture `enc:<id>:lit` holds only the lit pixels of the atlas, at the
 * same frames, and the frames that have any. Undefined for a day set, a set with none, or no WebGL.
 */
export function registerLitTexture(scene: Phaser.Scene, set: EncounterSet): { key: string; frames: ReadonlySet<string> } | undefined {
  if (!keepsLit(set) || !set.atlas || scene.game.renderer.type !== Phaser.WEBGL) return undefined;
  const at = framesOf(set);
  if (!at) return undefined;
  let frames = litFrames.get(set);
  const key = litTextureKey(set.id);
  if (!frames || !scene.textures.exists(key)) {
    const { rgba, width, height } = set.atlas;
    const lit = new Uint8ClampedArray(rgba.length);
    let any = false;
    for (let i = 0; i < rgba.length; i += 4) if (isLit(rgba, i)) { lit[i] = rgba[i]!; lit[i + 1] = rgba[i + 1]!; lit[i + 2] = rgba[i + 2]!; lit[i + 3] = 255; any = true; }
    const found = new Set<string>();
    if (any) for (const [frame, r] of at) {
      search: for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) if (lit[(y * width + x) * 4 + 3]) { found.add(frame); break search; }
    }
    frames = found;
    litFrames.set(set, frames);
    if (!found.size) return undefined;
    if (!scene.textures.exists(key)) {
      new DataTexture(scene, key, width, height, toGl(lit, width, height), false, true);
      const tex = scene.textures.get(key);
      for (const frame of found) { const r = at.get(frame)!; tex.add(frame, 0, r.x, r.y, r.w, r.h); }
    }
  }
  return frames.size ? { key, frames } : undefined;
}

/** `holder` no longer draws set `id`: its textures go once no one holds them (docs/186, performance). */
export function releaseSetTexture(scene: Phaser.Scene, id: string, holder: object): void {
  const key = textureKey(id);
  const h = holders.get(key);
  h?.delete(holder);
  if (h?.size) return;
  holders.delete(key);
  for (const k of [key, litTextureKey(id)]) if (scene.textures?.exists(k)) scene.textures.remove(k);
}
